/**
 * Billing endpoints — port of v1 ``ando-frontend/app/pages/BillingPage.tsx``
 * (143 LoC) + ``OrgBillingPage.tsx`` (477 LoC).
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/billing.py`` —
 * 35+ routes. This barrel ports the subset needed by the v2 Settings →
 * Billing surface; per-org enterprise flows can grow in-place.
 *
 *   GET  /billing/subscription   → current sub + limits + billing_mode
 *   POST /billing/portal         → Stripe customer portal redirect URL
 *   POST /billing/checkout       → Stripe Checkout session URL
 *   POST /billing/cancel         → cancel (at_period_end optional)
 *   GET  /billing/usage          → monthly chat + ingestion EUR + storage
 *   GET  /billing/usage/daily    → daily cost split (cap %)
 *   GET  /billing/usage/weekly   → weekly cost split (burst cap)
 *   GET  /billing/invoices       → invoice history
 *   GET  /billing/plan-limits    → resolved limits per active plan
 *   GET  /billing/features       → feature gate matrix (thinking/web/etc.)
 */

import { apiClient } from "../ApiClient.js";

// ── Subscription ───────────────────────────────────────────────────────

export interface BillingSubscriptionResponse {
  tier: string;
  status: string;
  is_trialing: boolean;
  cancel_at_period_end: boolean;
  current_period_start?: string | null;
  current_period_end?: string | null;
  stripe_enabled: boolean;
  limits: Record<string, unknown>;
  /** ``"individual"`` (Stripe self-service) or ``"invoice"`` (per-org enterprise). */
  billing_mode: "individual" | "invoice";
  /** May 2026 split-bucket: informational only — does NOT count against cap. */
  ingestion_used_eur?: number;
}

export async function getBillingSubscription(): Promise<BillingSubscriptionResponse> {
  return apiClient.get<BillingSubscriptionResponse>("/billing/subscription");
}

// ── Stripe portal / checkout ───────────────────────────────────────────

export interface PortalResponse {
  portal_url: string;
}

export interface CheckoutResponse {
  checkout_url: string;
  publishable_key: string;
}

export interface CheckoutRequest {
  /** ``pro`` or ``max``. ``enterprise`` requires contact-sales (BE rejects). */
  tier: "pro" | "max";
  billing_period?: "monthly" | "yearly";
}

export async function createBillingPortal(): Promise<PortalResponse> {
  return apiClient.post<PortalResponse>("/billing/portal", {});
}

export async function createBillingCheckout(
  body: CheckoutRequest,
): Promise<CheckoutResponse> {
  return apiClient.post<CheckoutResponse>("/billing/checkout", body);
}

// ── Cancel ─────────────────────────────────────────────────────────────

export interface CancelRequest {
  at_period_end?: boolean;
  reason?: string;
}

export async function cancelBillingSubscription(
  body: CancelRequest = {},
): Promise<{ success: boolean; message: string }> {
  return apiClient.post<{ success: boolean; message: string }>(
    "/billing/cancel",
    { at_period_end: body.at_period_end ?? true, reason: body.reason },
  );
}

// ── Usage ──────────────────────────────────────────────────────────────

export interface BillingUsageResponse {
  month: string;
  quick_queries_used: number;
  standard_queries_used: number;
  expert_queries_used: number;
  documents_indexed: number;
  documents_limit: number;
  storage_used_bytes: number;
  storage_limit_bytes: number;
  storage_pct: number;
  chat_used_eur: number;
  ingestion_used_eur: number;
  /** @deprecated alias of ``chat_used_eur`` retained for legacy bundles. */
  cost_used_eur: number;
  cost_limit_eur: number;
  cost_pct: number;
}

export interface DailyUsageResponse {
  date: string;
  chat_used_eur: number;
  ingestion_used_eur: number;
  cost_used_eur: number;
  cost_limit_eur: number;
  daily_usage_pct: number;
  monthly_usage_pct: number;
}

export interface WeeklyUsageResponse {
  week_start: string;
  chat_used_eur: number;
  ingestion_used_eur: number;
  cost_used_eur: number;
  cost_limit_eur: number;
  weekly_usage_pct: number;
}

export async function getBillingUsage(): Promise<BillingUsageResponse> {
  return apiClient.get<BillingUsageResponse>("/billing/usage");
}

export async function getDailyBillingUsage(): Promise<DailyUsageResponse> {
  return apiClient.get<DailyUsageResponse>("/billing/usage/daily");
}

export async function getWeeklyBillingUsage(): Promise<WeeklyUsageResponse> {
  return apiClient.get<WeeklyUsageResponse>("/billing/usage/weekly");
}

// ── Invoices ───────────────────────────────────────────────────────────

export interface InvoiceItem {
  id: string;
  number?: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  created: string;
  period_start?: string | null;
  period_end?: string | null;
  invoice_pdf?: string | null;
  hosted_invoice_url?: string | null;
}

export async function listBillingInvoices(): Promise<InvoiceItem[]> {
  return apiClient.get<InvoiceItem[]>("/billing/invoices");
}

// ── Feature access (gates composer toggles) ────────────────────────────

export interface BillingFeatureAccess {
  thinking_mode: boolean;
  web_search: boolean;
  expert_queries: boolean;
  [feature: string]: boolean;
}

export async function getBillingFeatureAccess(): Promise<BillingFeatureAccess> {
  return apiClient.get<BillingFeatureAccess>("/billing/features");
}

// ── Credits (pay-as-you-go top-ups) ────────────────────────────────────
// BE: ``GET /billing/credits``, ``GET /billing/credits/products``,
//     ``POST /billing/credits/purchase``.

export interface BillingCreditBalance {
  balance: number;
  lifetime_purchased: number;
  lifetime_used: number;
  auto_topup_enabled: boolean;
  auto_topup_threshold?: number | null;
  auto_topup_amount?: number | null;
  last_purchase_at?: string | null;
  last_usage_at?: string | null;
}

export interface BillingTopupProduct {
  id: number;
  name: string;
  /** EUR face value charged via Stripe. */
  amount: number;
  /** Credits received (1€ = 1 credit, larger packs include a bonus). */
  credits: number;
  stripe_price_id?: string | null;
}

export interface PurchaseCreditsRequest {
  product_id: number;
  success_url?: string;
  cancel_url?: string;
}

export interface PurchaseCreditsResponse {
  checkout_url: string;
  session_id?: string;
}

export async function getBillingCreditBalance(): Promise<BillingCreditBalance> {
  return apiClient.get<BillingCreditBalance>("/billing/credits");
}

export async function getBillingTopupProducts(): Promise<BillingTopupProduct[]> {
  return apiClient.get<BillingTopupProduct[]>("/billing/credits/products");
}

export async function purchaseBillingCredits(
  body: PurchaseCreditsRequest,
): Promise<PurchaseCreditsResponse> {
  return apiClient.post<PurchaseCreditsResponse>(
    "/billing/credits/purchase",
    body,
  );
}

// ── Usage alerts (proactive 70 / 90% warnings) ─────────────────────────
// BE: ``GET /billing/usage/alerts``.

export interface BillingUsageAlert {
  metric: string;
  current: number;
  limit: number;
  percentage: number;
  severity: "warning" | "critical";
  message: string;
}

export interface BillingUsageAlertsResponse {
  alerts: BillingUsageAlert[];
  has_critical: boolean;
}

export async function getBillingUsageAlerts(): Promise<BillingUsageAlertsResponse> {
  return apiClient.get<BillingUsageAlertsResponse>("/billing/usage/alerts");
}
